import { replaceVerbatim } from "@project/shared/src/replaceVerbatim";
import * as THREE from "three";
import { ALUMINUM_MATERIAL_PARAMS } from "./AluminumMaterial";
import {
  heightMapToNormalMap,
  heightMapToReliefMap,
  heightMapToRoughnessMap,
  type TextureExtent,
  textureExtent,
} from "./proceduralMaps";

/**
 * Procedural surface finish for the extruded aluminium parts — the Kantenschutz
 * (angle sections) and the closing profiles.
 *
 * Two patterns, both running parallel to the extrusion axis because that is the
 * only direction the die can leave a mark in:
 *
 *  - The designed **grooves** of the cross-section, a flat face with narrow
 *    grooves cut into it at a regular spacing (`‾‾‾\_/‾‾‾\_/‾‾‾`): three per
 *    leg on the Kantenschutz, two per outer face on the closing profiles.
 *  - The **die lines** and **die streaks** every extrusion carries whether or
 *    not anybody asked for them. See {@link DIE_LINE_PITCH_MM}.
 *
 * Baked into a normal map and a roughness map, scaled to physical millimetres
 * via `texture.repeat`.
 */

/**
 * Groove centres across a leg, as a fraction of its depth measured from the
 * bend of the angle.
 *
 * Three per face: one down the middle of the leg with one either side at the
 * same spacing. Expressed as a centre and an offset rather than as three
 * separate numbers, so the outer pair stays symmetrical about the middle one
 * whichever value is tuned.
 *
 * Fractions of the leg rather than millimetres, so they scale with the profile
 * size.
 */
const CENTRE_GROOVE_POSITION = 0.5;
const GROOVE_SPACING = 0.35;

const GROOVE_POSITIONS = [
  CENTRE_GROOVE_POSITION - GROOVE_SPACING,
  CENTRE_GROOVE_POSITION,
  CENTRE_GROOVE_POSITION + GROOVE_SPACING,
];

/**
 * Groove centres along an outer face of a closing profile, as fractions of
 * that face: two, centred, one Kantenschutz spacing apart. Exported for
 * `ProfileRenderer`, which turns them into positions on the developed section.
 */
export const PROFILE_GROOVE_POSITIONS: readonly number[] = [
  CENTRE_GROOVE_POSITION - GROOVE_SPACING / 2,
  CENTRE_GROOVE_POSITION + GROOVE_SPACING / 2,
];

/** Half-width of a groove flank, in millimetres. */
const GROOVE_HALF_WIDTH_MM = 0.9;

/**
 * Depth of a groove, in millimetres — the unit the height field below is
 * written in, made physical.
 *
 * The normal map never needed this number: its tilt is a slope, tuned by eye
 * through `normalIntensity`. Parallax does, because it moves the texture by a
 * real distance for a real depth, and a groove that slid a millimetre for
 * three tenths of relief would read as a cardboard cut-out. A few tenths is
 * what the sections in the catalogue show.
 */
const GROOVE_DEPTH_MM = 0.3;

/**
 * Texels across one tile.
 *
 * A groove flank is `GROOVE_HALF_WIDTH_MM / depth` of a tile — on a 25 mm leg
 * about 3.6%, which at 256 was nine texels to carry the whole curve. That is
 * enough for the straight ramp this used to bake, because linear interpolation
 * reproduces a straight line exactly, but not for the rounded one below, whose
 * shape lives entirely in how it bends.
 *
 * The bake is O(size^2) and synchronous on the main thread, so this is not free
 * — but it runs once per distinct section and is cached from then on.
 */
const TEXTURE_SIZE = 512;

/**
 * Texels for a closing profile's bake: a strip, not a square.
 *
 * Its tile has to span the whole developed section — around 120 mm on a 7 mm
 * hybrid profile — because the grooves sit at absolute positions on it, the
 * same way the Kantenschutz stretches one tile across a leg. At 512 texels that
 * is 0.23 mm a texel, every die line floors out at {@link DIE_LINE_MIN_TEXELS}
 * and the finish turns to corduroy. The pattern only varies across the section
 * though, so the texels can all go along U: 4096 across is 0.03 mm a texel,
 * finer than the 20 mm tile this used to repeat, at an eighth of the memory.
 * Four rows are enough for a mip chain; the maps repeat along V.
 */
const PROFILE_TEXTURE_EXTENT = { width: 4096, height: 4 } as const;

/**
 * Die lines: the fine longitudinal marks that make an extrusion look extruded.
 *
 * Aluminium is pushed through a steel die, and the bearing surfaces of that die
 * are never perfectly smooth — they carry machining marks, they pick up
 * aluminium and oxide as metal flows over them, and they wear. Every
 * irregularity on a bearing is dragged along the whole length of the section as
 * a shallow depression or a raised line, continuous from one end to the other,
 * at an irregular spacing and an irregular depth. Micro die lines survive even
 * polished, nitrided bearings; they are a property of the process, not a
 * defect anyone can specify away.
 *
 * What this buys visually is anisotropy: an extrusion reflects differently
 * along its length than across it, and without that a profile reads as an
 * extruded *solid* — a swept cylinder — rather than as drawn metal.
 *
 * The numbers are a compromise with the texture, not a measurement. Real die
 * lines are a few micrometres deep and can be far finer than
 * {@link DIE_LINE_MIN_HALF_WIDTH_MM}; a line the texture cannot resolve is
 * aliasing rather than detail, so the widths are floored at
 * {@link DIE_LINE_MIN_TEXELS} and the pattern is drawn slightly coarser and
 * slightly shallower than life.
 *
 * Depths are fractions of a groove depth, which is the unit the height field
 * below is written in. A Kantenschutz groove is a few tenths of a millimetre,
 * so 0.004..0.02 of one puts these in the single-digit micrometre range they
 * belong in.
 */
const DIE_LINE_PITCH_MM = 0.8;
const DIE_LINE_MIN_COUNT = 8;
/**
 * Lines start touching at `texels / (2 * DIE_LINE_MIN_TEXELS)` — 64 on a
 * 512-texel tile — once a floored flank either side of one line reaches its
 * neighbour and the pattern washes out into corduroy. Three quarters of that
 * leaves headroom for the jitter: 48 at 512, 384 on the profile strip.
 */
function dieLineMaxCount(texelsAcross: number): number {
  return Math.floor((texelsAcross / (2 * DIE_LINE_MIN_TEXELS)) * 0.75);
}
const DIE_LINE_MIN_HALF_WIDTH_MM = 0.16;
const DIE_LINE_MAX_HALF_WIDTH_MM = 0.55;
const DIE_LINE_MIN_DEPTH = 0.004;
const DIE_LINE_MAX_DEPTH = 0.02;
/**
 * Share of the lines that stand proud of the face instead of cutting into it.
 * A bearing that has picked up metal leaves a scratch; one that has been nicked
 * leaves a ridge, and the two are not equally common.
 */
const DIE_LINE_RIDGE_FRACTION = 0.3;
/** Texel floor on a flank, so smoothstep has something to be smooth across. */
const DIE_LINE_MIN_TEXELS = 4;

/**
 * Die streaks: the other longitudinal marking, and a different thing entirely.
 *
 * A die *line* is topography. A die *streak* is tonal — a band of slightly
 * different lustre, of fairly uniform width, whose cause is metallurgical
 * rather than mechanical: intermetallics and inclusions from the billet, or
 * uneven cooling where the section changes wall thickness, leaving a strip that
 * takes the light differently. It has no depth to put in a normal map, so it
 * goes in the roughness map instead, which is exactly what it is.
 *
 * Wide and shallow on purpose: this is a sheen variation, and at more than a
 * few hundredths of roughness it stops reading as metal and starts reading as
 * dirt.
 */
const DIE_STREAK_PITCH_MM = 7;
const DIE_STREAK_MIN_COUNT = 3;
const DIE_STREAK_MAX_COUNT = 12;
const DIE_STREAK_ROUGHNESS_DELTA = 0.045;

/**
 * Base roughness the die streaks vary around, shared by every extruded part.
 *
 * One number for the profiles and the Kantenschutz alike: the Kantenschutz is
 * not a decorative trim piece, it comes off the same kind of press with the
 * same mill finish, and its grooves are a feature of the section rather than a
 * reason to shade it any brighter.
 *
 * Above `ALUMINUM_MATERIAL_PARAMS.roughness`, which this deliberately does not
 * read. That value covers imported aluminium hardware; bare extrusion is duller
 * than it, and at the shared 0.22 a flat profile face mirrors the 512-pixel
 * environment map closely enough to read as chrome rather than as metal.
 *
 * It lives here because `heightMapToRoughnessMap` bakes absolute values and the
 * material therefore has to run at `roughness = 1` — so this is the only place
 * the real number survives.
 */
const EXTRUDED_ROUGHNESS = 0.32;

/**
 * How much wider the specular lobe is across the die lines than along them,
 * 0 to 1. Every mark on an extrusion is a streak along its length, so the
 * micro-normals scatter across the length and hardly at all along it: the
 * highlight on a real profile is a band stretched across the section, not a
 * spot. The baked normal map carries the individual lines, but at the map's
 * resolution it cannot carry the sub-texel scatter between them, which is
 * what this stands in for.
 *
 * three folds it in as `alphaT = mix(roughness², 1, anisotropy²)`: at 0.25 on
 * the 0.32 roughness the across-line roughness comes to 0.40, along-line
 * stays 0.32. At 0.4 it was 0.50, and that is a brushed finish: the lobe was
 * wide enough that every point of a 20 mm rounded section caught the key
 * softbox at once, and the profiles rendered as a white band with the die
 * lines gone. `MeshPhysicalMaterial` is what carries it; its other lobes stay
 * off, so the cost over the standard material is the anisotropic GGX alone.
 */
const EXTRUDED_ANISOTROPY = 0.25;

/** Which UV axis the pattern varies along. */
type GrooveAxis = "u" | "v";

type SurfaceTextureConfig = Readonly<{
  axis: GrooveAxis;
  /** Texels of the bake; see {@link TEXTURE_SIZE}, {@link PROFILE_TEXTURE_EXTENT}. */
  extent: TextureExtent;
  /** Height-map gradient strength when baking the normal map. */
  normalIntensity: number;
  /** Material normalScale multiplier applied in the shader. */
  normalScale: number;
}>;

// Kantenschutz: larger flat faces — pattern can read a bit stronger.
//
// Strength is split between the two stages on purpose. `normalIntensity` is the
// slope baked into the map, and a narrow groove is already steep, so an
// intensity much above 2.5 saturates the encoding (`dz` in heightMapToNormalMap
// reaches zero and the normal turns fully sideways). The rest of the strength
// comes from `normalScale`, which multiplies in the shader and cannot saturate.
//
// `normalIntensity` is 2.7 rather than 2 to hold the baked map still across two
// changes that would otherwise move it, neither of them intentional:
//
//   2 x (512 / 256) / 1.5 = 2.67
//
// heightMapToNormalMap takes a central difference between neighbouring texels
// and does not normalise by texel size, so its output scales with 1 / size —
// doubling TEXTURE_SIZE alone would have halved the groove. Smoothstep then
// pulls the other way: its slope peaks at 1.5x the straight ramp it replaces.
// With both compensated the bake is byte-for-byte as strong as it was, at every
// leg depth, so nothing newly saturates the encoding.
//
// `normalScale` is the deliberate change: 3 rather than 4.5 is a third off the
// pattern's strength in the shader, where shimmer scales with it directly and
// nothing can saturate.
const ANGLE_SECTION_SURFACE_CONFIG: SurfaceTextureConfig = {
  axis: "v",
  extent: TEXTURE_SIZE,
  normalIntensity: 2.7,
  normalScale: 3,
};

/**
 * The texel the Kantenschutz intensity above was tuned against: a 25 mm leg
 * over a 512-texel tile.
 *
 * `heightMapToNormalMap` differences neighbouring texels without dividing by
 * their size, so the slope it bakes for a given physical groove scales with
 * the texel: a groove spread over twice as many texels bakes at half the tilt.
 * The Kantenschutz lives with that across its range of leg depths; the profile
 * strip's texels are a good deal finer, and without this reference its grooves
 * would come out visibly softer than the ones next to them on the corner.
 */
const REFERENCE_TEXEL_MM = 25 / TEXTURE_SIZE;

// Closing profiles: the same groove as the Kantenschutz, so the same shader
// multiplier, and the same die-line compensation for it. The intensity is
// scaled per section so that the baked groove matches the Kantenschutz one
// texel for texel — see {@link REFERENCE_TEXEL_MM}.
//
// The axis is U because these are `ProfiledContourGeometry`, whose U runs
// across the cross-section and whose V runs along the extrusion; the angle
// section's `ExtrudeGeometry` caps are the other way round.
function profileSurfaceConfig(spanMm: number): SurfaceTextureConfig {
  const texelMm = spanMm / PROFILE_TEXTURE_EXTENT.width;
  return {
    axis: "u",
    extent: PROFILE_TEXTURE_EXTENT,
    normalIntensity:
      ANGLE_SECTION_SURFACE_CONFIG.normalIntensity *
      (REFERENCE_TEXEL_MM / texelMm),
    normalScale: ANGLE_SECTION_SURFACE_CONFIG.normalScale,
  };
}

/**
 * Everything about one cross-section that changes what gets baked, in the units
 * the bake works in.
 */
interface SurfaceSpec {
  /**
   * Developed width of the textured cross-section in millimetres — one texture
   * tile spans exactly this, which is what lets a pattern specified in
   * millimetres be placed in normalised tile coordinates.
   */
  spanMm: number;
  /** Groove centres, as fractions of the span. */
  groovePositions: readonly number[];
  /** Groove flank half-width, as a fraction of the span. */
  grooveHalfWidth: number;
  /**
   * Multiplier on the die-line depths, set to `1 / normalScale`.
   *
   * The shader multiply is chosen by how strongly the *grooves* want to read,
   * and it applies to the whole normal map. Without this compensation the same
   * surface finish would come out three times as coarse on a Kantenschutz as on
   * a profile, which is backwards — they come off similar dies.
   */
  dieLineAmplitude: number;
}

/**
 * Baked surface maps, keyed by the section that produced them.
 *
 * Bounded like the foam and coated-panel caches: an entry holds two 512x512
 * canvases and their GPU textures, and the key varies with the section size, so
 * an editor that lets a dimension be typed freely would otherwise retain a set
 * of those per keystroke for the lifetime of the page.
 */
const TEXTURE_CACHE_LIMIT = 8;

interface SurfaceMaps {
  normalMap: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  /**
   * The height field itself, 1 at its highest point and 0 at its lowest, for
   * {@link applyExtrudedParallax}. Sampled with the normal map's UV, so it
   * carries no transform of its own.
   */
  reliefMap: THREE.CanvasTexture;
  /** Millimetres between relief 0 and relief 1. */
  reliefDepthMm: number;
}

const textureCache = new Map<string, SurfaceMaps>();

/**
 * Smoothstep on [0, 1]. Zero slope at both ends is the only property that
 * matters here — see {@link grooveHeight}.
 */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Deterministic value in [0, 1) for line `index` on channel `salt`.
 *
 * Not `Math.random`: a die is a fixed piece of steel, so the same section has
 * to come out with the same marks in every session and on every machine — and
 * the texture cache keys on the section alone, so two profiles that share a key
 * had better share a pattern.
 */
function scatter(index: number, salt: number): number {
  // Offset off zero: `sin(0)` is 0, so the first line on the first channel would
  // get exactly the same value every time — always the same jitter, always hard
  // against the start of the tile, on every profile in the scene.
  const value =
    Math.sin((index + 1) * 12.9898 + (salt + 1) * 4.1414) * 43758.5453;
  return value - Math.floor(value);
}

/** Lines that fit across `spanMm` at `pitchMm`, clamped to what a tile can hold. */
function lineCount(
  spanMm: number,
  pitchMm: number,
  minimum: number,
  maximum: number,
): number {
  return Math.min(maximum, Math.max(minimum, Math.round(spanMm / pitchMm)));
}

interface DieLine {
  /** Position across the section, as a fraction of the span. */
  centre: number;
  /** Half-width of the flank, in millimetres. */
  halfWidthMm: number;
  /** Signed depth, in groove depths: negative cuts in, positive stands proud. */
  depth: number;
}

/**
 * The die lines for one section.
 *
 * Placed on a jittered grid rather than at independent random positions: the
 * jitter is under half a cell, so no two lines can swap order or land on top of
 * each other, and the spacing still varies by more than an order of magnitude
 * across the section — which is what irregular looks like. Independent
 * positions would clump instead, and a clump of coincident lines is one deep
 * groove, not a die line.
 */
function buildDieLines(spanMm: number, texelsAcross: number): DieLine[] {
  const count = lineCount(
    spanMm,
    DIE_LINE_PITCH_MM,
    DIE_LINE_MIN_COUNT,
    dieLineMaxCount(texelsAcross),
  );
  const lines: DieLine[] = [];
  for (let index = 0; index < count; index++) {
    const jitter = (scatter(index, 0) - 0.5) * 0.9;
    const depth =
      DIE_LINE_MIN_DEPTH +
      (DIE_LINE_MAX_DEPTH - DIE_LINE_MIN_DEPTH) * scatter(index, 2);
    lines.push({
      centre: (index + 0.5 + jitter) / count,
      halfWidthMm:
        DIE_LINE_MIN_HALF_WIDTH_MM +
        (DIE_LINE_MAX_HALF_WIDTH_MM - DIE_LINE_MIN_HALF_WIDTH_MM) *
          scatter(index, 1),
      depth: scatter(index, 3) < DIE_LINE_RIDGE_FRACTION ? depth : -depth,
    });
  }
  return lines;
}

interface DieStreak {
  /** Position across the section, as a fraction of the span. */
  centre: number;
  /** Half-width, as a fraction of the span. */
  halfWidth: number;
  /** Signed lustre offset in [-1, 1]; positive is the duller band. */
  amplitude: number;
}

function buildDieStreaks(spanMm: number): DieStreak[] {
  const count = lineCount(
    spanMm,
    DIE_STREAK_PITCH_MM,
    DIE_STREAK_MIN_COUNT,
    DIE_STREAK_MAX_COUNT,
  );
  const streaks: DieStreak[] = [];
  for (let index = 0; index < count; index++) {
    const jitter = (scatter(index, 4) - 0.5) * 0.9;
    streaks.push({
      centre: (index + 0.5 + jitter) / count,
      // A fraction of the pitch, so the widest streaks just touch their
      // neighbours and the rest leave bare face between them. Wider than half
      // a pitch and every point of the section is inside some streak, which is
      // not a streak at all — it is one smooth swell, and it also means the
      // field never reaches zero, so nothing is left at the base roughness.
      halfWidth: (0.25 + 0.35 * scatter(index, 5)) / count,
      amplitude: scatter(index, 6) * 2 - 1,
    });
  }
  return streaks;
}

/**
 * Distance from `t` to `centre` the short way round the section.
 *
 * A profile cross-section is a closed loop, so its developed U wraps: the
 * pattern has to be periodic in the tile or there is a visible seam down the
 * profile where U = 1 meets U = 0. It costs nothing on the angle section, whose
 * axis is clamped and whose lines never reach the ends anyway.
 */
function cyclicDistance(t: number, centre: number): number {
  const delta = Math.abs(t - centre);
  return Math.min(delta, 1 - delta);
}

/**
 * Height across the section at `t` (0 at the inner corner of a leg, 1 at the
 * free edge): flat face with a narrow groove cut into it at each groove
 * position (`‾‾‾\_/‾‾‾`). Negative values are below the face, which is what
 * makes it a groove and not a rib — the two shade as opposites.
 *
 * The flank is a smoothstep and not the straight ramp it reads as in section,
 * because a straight ramp meets the flat face and its own apex at a corner. A
 * corner in the height field is a step in the *normal*, and a step has no
 * bandwidth limit: no texture resolution, mip level or anisotropy setting can
 * represent it without aliasing, and on aluminium's tight specular lobe it
 * showed up as the staircase along the grooves. Smoothstep is the cheapest
 * curve that lands at zero slope on both ends, so the normal now arrives at the
 * flat face and leaves the apex continuously and the pattern is band-limited.
 *
 * The cost is a groove that is rounded rather than crisply cut. An extruded
 * section is drawn through a die and has a radius at both, so this is arguably
 * the more honest shape anyway.
 */
function grooveHeight(
  t: number,
  halfWidth: number,
  positions: readonly number[],
): number {
  let height = 0;
  for (const centre of positions) {
    const distance = Math.abs(t - centre);
    if (distance < halfWidth) {
      height = Math.min(height, -(1 - smoothstep(distance / halfWidth)));
    }
  }
  return height;
}

/**
 * Die-line relief at `t`, in groove depths.
 *
 * Summed rather than combined with `min` as the grooves are: two grooves at the
 * same place are still one groove, but two irregularities on the same part of
 * the bearing genuinely do add up, and the occasional deeper line where they
 * overlap is the point.
 */
function dieLineHeight(
  t: number,
  lines: readonly DieLine[],
  spanMm: number,
  texelsAcross: number,
): number {
  const minHalfWidth = DIE_LINE_MIN_TEXELS / texelsAcross;
  let height = 0;
  for (const line of lines) {
    const halfWidth = Math.max(line.halfWidthMm / spanMm, minHalfWidth);
    const distance = cyclicDistance(t, line.centre);
    if (distance < halfWidth) {
      height += line.depth * (1 - smoothstep(distance / halfWidth));
    }
  }
  return height;
}

/** Die-streak lustre offset at `t`, in [-1, 1]. */
function streakTone(t: number, streaks: readonly DieStreak[]): number {
  let tone = 0;
  for (const streak of streaks) {
    const distance = cyclicDistance(t, streak.centre);
    if (distance < streak.halfWidth) {
      tone += streak.amplitude * (1 - smoothstep(distance / streak.halfWidth));
    }
  }
  return Math.min(1, Math.max(-1, tone));
}

/**
 * The two fields behind the two maps. Both vary only across the section and are
 * constant along the extrusion, which is what makes every mark a streak running
 * the full length of the part rather than a blotch on it.
 */
function buildSurfaceFields(
  extent: { width: number; height: number },
  axis: GrooveAxis,
  spec: SurfaceSpec,
): { relief: Float32Array; tone: Float32Array } {
  const { width, height } = extent;
  // Texels along the axis the pattern varies on, and along the other one.
  const across = axis === "v" ? height : width;
  const along = axis === "v" ? width : height;
  const lines = buildDieLines(spec.spanMm, across);
  const streaks = buildDieStreaks(spec.spanMm);
  const relief = new Float32Array(width * height);
  const tone = new Float32Array(width * height);

  for (let i = 0; i < across; i++) {
    // `i` walks the axis the pattern varies along. For "v" that is the row
    // index, and CanvasTexture leaves flipY on, so row 0 is sampled at v = 1
    // and the position has to be complemented or the whole pattern is mirrored
    // across the leg and every position means its complement. Columns are not
    // flipped, so "u" reads straight.
    const t = axis === "v" ? 1 - (i + 0.5) / across : (i + 0.5) / across;
    const relief1d =
      grooveHeight(t, spec.grooveHalfWidth, spec.groovePositions) +
      dieLineHeight(t, lines, spec.spanMm, across) * spec.dieLineAmplitude;
    const shade = streakTone(t, streaks);
    for (let j = 0; j < along; j++) {
      // Row-major, `width` texels a row: "u" varies along the row (column i,
      // row j), "v" down the rows (row i, column j).
      const index = axis === "u" ? j * width + i : i * width + j;
      relief[index] = relief1d;
      tone[index] = shade;
    }
  }

  return { relief, tone };
}

/**
 * Absolute roughness per texel, with the extremes it actually reached.
 *
 * `heightMapToRoughnessMap` stretches whatever field it is given onto the
 * `[low, high]` it is asked for, which is right for a height field — whose zero
 * is its maximum, the untouched face — and wrong for this one, whose zero is
 * the bare face sitting *inside* the range. Handing it a nominal
 * `EXTRUDED_ROUGHNESS ± DIE_STREAK_ROUGHNESS_DELTA` therefore did not produce
 * that: it produced whatever spread the hash happened to reach, stretched to
 * fill it. Two legs of one angle meant to share a mill finish came out with
 * visibly different roughness purely from where the extremes landed.
 *
 * Passing the field's own min and max makes that stretch an identity, so the
 * map carries exactly `EXTRUDED_ROUGHNESS + DIE_STREAK_ROUGHNESS_DELTA * tone`
 * and a face with no streak on it is exactly {@link EXTRUDED_ROUGHNESS}.
 */
function toRoughnessField(tone: Float32Array): {
  field: Float32Array;
  low: number;
  high: number;
} {
  const field = new Float32Array(tone.length);
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < tone.length; i++) {
    const value =
      EXTRUDED_ROUGHNESS + DIE_STREAK_ROUGHNESS_DELTA * (tone[i] ?? 0);
    field[i] = value;
    if (value < low) low = value;
    if (value > high) high = value;
  }
  return { field, low, high };
}

function getSurfaceMaps(
  config: SurfaceTextureConfig,
  spec: SurfaceSpec,
): SurfaceMaps {
  const extent = textureExtent(config.extent);
  const key = [
    "alu-surface",
    config.axis,
    `${extent.width}x${extent.height}`,
    config.normalIntensity,
    spec.spanMm,
    spec.grooveHalfWidth,
    spec.groovePositions.join(","),
    spec.dieLineAmplitude,
  ].join("-");
  const cached = textureCache.get(key);
  if (cached) {
    // Re-insert so the eviction below is least-recently-*used* and not merely
    // oldest-inserted. The profile's own entry is written once and then hit
    // constantly, so under plain FIFO a product with eight distinct angle
    // section depths ages it out first and re-bakes two 512x512 maps on the
    // main thread at the next profile mount — and evicts the next live entry
    // doing it, which thrashes for the rest of the session.
    textureCache.delete(key);
    textureCache.set(key, cached);
    return cloneSurfaceMaps(cached);
  }

  const { relief, tone } = buildSurfaceFields(extent, config.axis, spec);
  const roughness = toRoughnessField(tone);
  let lowest = Number.POSITIVE_INFINITY;
  let highest = Number.NEGATIVE_INFINITY;
  for (const value of relief) {
    if (value < lowest) lowest = value;
    if (value > highest) highest = value;
  }
  const maps: SurfaceMaps = {
    normalMap: heightMapToNormalMap(extent, relief, config.normalIntensity),
    // Its own extremes as the ramp, which makes `heightMapToRoughnessMap`'s
    // renormalisation the identity — see {@link toRoughnessField}.
    roughnessMap: heightMapToRoughnessMap(
      extent,
      roughness.field,
      roughness.low,
      roughness.high,
    ),
    // Normalised to the field's own extremes, like the roughness map; the
    // depth those extremes span is remembered beside it, in millimetres.
    reliefMap: heightMapToReliefMap(extent, relief),
    reliefDepthMm: (highest - lowest) * GROOVE_DEPTH_MM,
  };

  if (textureCache.size >= TEXTURE_CACHE_LIMIT) {
    // Map iterates in insertion order, so this is the oldest entry. Only the
    // master is freed; the clones handed out from it own their own lifetimes.
    const oldest = textureCache.keys().next().value;
    const evicted = oldest === undefined ? undefined : textureCache.get(oldest);
    if (oldest !== undefined) textureCache.delete(oldest);
    evicted?.normalMap.dispose();
    evicted?.roughnessMap.dispose();
    evicted?.reliefMap.dispose();
  }
  textureCache.set(key, maps);
  return cloneSurfaceMaps(maps);
}

/** Clones share image data with the master but own their UV transforms. */
function cloneSurfaceMaps(maps: SurfaceMaps): SurfaceMaps {
  return {
    normalMap: maps.normalMap.clone(),
    roughnessMap: maps.roughnessMap.clone(),
    reliefMap: maps.reliefMap.clone(),
    reliefDepthMm: maps.reliefDepthMm,
  };
}

/**
 * Parallax on top of the normal map.
 *
 * A normal map tilts the shading but leaves every texel where the geometry
 * put it, so a groove looks the same from straight on and from a glancing
 * angle — which is exactly the angle a profile is mostly seen at. Parallax
 * shifts the sample along the eye's tangent-space direction by how far the
 * surface there lies below the face, so the far wall of a groove hides behind
 * its near edge as the case turns. Two refinement steps (sample the depth,
 * shift, sample again at the shifted spot) — the relief is shallow and smooth,
 * so a full occlusion march would buy nothing for its cost.
 *
 * Offset-limited: the division by the view's Z is clamped, because past about
 * 75° from the normal the true offset grows without bound while the geometry
 * it belongs to has shrunk to a line, and the texture would stream across it.
 *
 * `roughnessmap_fragment` is moved to after `normal_fragment_begin` so both
 * maps can share the shifted coordinate; nothing between the two reads
 * `roughnessFactor`. The relief is sampled with the *normal map's* UV, so it
 * inherits that map's transform and needs none of its own.
 */
const PARALLAX_PARS_GLSL = /* glsl */ `
uniform sampler2D reliefMap;
uniform vec2 parallaxScale;
`;

const PARALLAX_UV_GLSL = /* glsl */ `
vec2 parallaxUv = vNormalMapUv;
#if defined( USE_NORMALMAP_TANGENTSPACE ) && ! defined( FLAT_SHADED )
{
	// tbn is in view space; vViewPosition points from the surface to the eye.
	// T and B come back scaled to the uv derivatives, so normalise each.
	vec3 eye = normalize( vViewPosition );
	vec3 eyeTangent = vec3(
		dot( eye, normalize( tbn[ 0 ] ) ),
		dot( eye, normalize( tbn[ 1 ] ) ),
		dot( eye, tbn[ 2 ] )
	);
	vec2 slide = eyeTangent.xy / max( eyeTangent.z, 0.25 ) * parallaxScale;
	float depth = 1.0 - texture2D( reliefMap, parallaxUv ).g;
	parallaxUv = vNormalMapUv - slide * depth;
	depth = 0.5 * ( depth + 1.0 - texture2D( reliefMap, parallaxUv ).g );
	parallaxUv = vNormalMapUv - slide * depth;
}
#endif
`;

const ROUGHNESS_INCLUDE = "#include <roughnessmap_fragment>";
const NORMAL_BEGIN_INCLUDE = "#include <normal_fragment_begin>";
const NORMAL_MAPS_INCLUDE = "#include <normal_fragment_maps>";
const NORMALMAP_PARS_INCLUDE = "#include <normalmap_pars_fragment>";

/**
 * Rewrites a physical material's fragment shader for {@link applyExtrudedParallax}.
 * Exported for the test that pins it to the shader three ships.
 */
export function parallaxFragmentShader(fragmentShader: string): string {
  for (const anchor of [
    ROUGHNESS_INCLUDE,
    NORMAL_BEGIN_INCLUDE,
    NORMAL_MAPS_INCLUDE,
    NORMALMAP_PARS_INCLUDE,
  ]) {
    if (!fragmentShader.includes(anchor)) {
      throw new Error(`Parallax patch: shader has no ${anchor}`);
    }
  }
  const roughness = THREE.ShaderChunk.roughnessmap_fragment.replace(
    "vRoughnessMapUv",
    "parallaxUv",
  );
  const normalMaps = THREE.ShaderChunk.normal_fragment_maps.replaceAll(
    "vNormalMapUv",
    "parallaxUv",
  );
  // `replaceVerbatim` for every computed replacement: these are interpolated
  // GLSL, and a string replacement would expand `$&`, `$1` and `$'` out of the
  // inserted source instead of inserting it as written.
  let patched = replaceVerbatim(
    fragmentShader,
    NORMALMAP_PARS_INCLUDE,
    `${NORMALMAP_PARS_INCLUDE}\n${PARALLAX_PARS_GLSL}`,
  );
  patched = patched.replace(ROUGHNESS_INCLUDE, "");
  patched = replaceVerbatim(
    patched,
    NORMAL_BEGIN_INCLUDE,
    `${NORMAL_BEGIN_INCLUDE}\n${PARALLAX_UV_GLSL}\n${roughness}`,
  );
  return replaceVerbatim(patched, NORMAL_MAPS_INCLUDE, normalMaps);
}

/**
 * One program for every extruded part: the patched source is identical, only
 * the uniforms differ. Three's default key stringifies `onBeforeCompile`,
 * which is a fresh closure per material and would compile one program each.
 */
const PARALLAX_PROGRAM_KEY = "extruded-aluminium-parallax";

/** The relief textures behind each material, for disposal. */
const parallaxMaps = new WeakMap<THREE.Material, THREE.Texture>();

/**
 * @param mmPerUv How many millimetres one unit of the normal map's transformed
 *   UV spans, per axis — what turns a depth in millimetres into a shift in
 *   texture space. Zero on an axis the pattern does not vary along.
 */
function applyExtrudedParallax(
  material: THREE.MeshPhysicalMaterial,
  maps: SurfaceMaps,
  mmPerUv: { u: number; v: number },
): void {
  const uniforms = {
    reliefMap: { value: maps.reliefMap },
    parallaxScale: {
      value: new THREE.Vector2(
        mmPerUv.u > 0 ? maps.reliefDepthMm / mmPerUv.u : 0,
        mmPerUv.v > 0 ? maps.reliefDepthMm / mmPerUv.v : 0,
      ),
    },
  };
  parallaxMaps.set(material, maps.reliefMap);
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.fragmentShader = parallaxFragmentShader(shader.fragmentShader);
  };
  material.customProgramCacheKey = () => PARALLAX_PROGRAM_KEY;
}

/**
 * `roughness` is 1 because `heightMapToRoughnessMap` bakes absolute values and
 * three multiplies the two together — {@link EXTRUDED_ROUGHNESS} is already
 * folded into the map. Overriding it here rather than leaving it out is
 * deliberate: the spread comes from {@link ALUMINUM_MATERIAL_PARAMS}, which
 * carries a roughness of its own, so a shrug at the order of these two lines
 * would silently darken the finish by 78%.
 */
function createExtrudedAluminumMaterial(
  maps: SurfaceMaps,
  config: SurfaceTextureConfig,
  mmPerUv: { u: number; v: number },
): THREE.MeshPhysicalMaterial {
  const material = new THREE.MeshPhysicalMaterial({
    ...ALUMINUM_MATERIAL_PARAMS,
    normalMap: maps.normalMap,
    normalScale: new THREE.Vector2(config.normalScale, config.normalScale),
    roughnessMap: maps.roughnessMap,
    roughness: 1,
    anisotropy: EXTRUDED_ANISOTROPY,
    // The direction of the *wider* lobe, in tangent space, measured from U.
    // Die lines run along the extrusion, so the surface is rougher across
    // them: the lobe is wide along the axis the pattern varies on, which is
    // exactly the axis the bake records.
    anisotropyRotation: config.axis === "u" ? 0 : Math.PI / 2,
  });
  applyExtrudedParallax(material, maps, mmPerUv);
  return material;
}

/**
 * Frees a material returned by one of the factories below.
 *
 * The factories hand out an owned variant rather than the cached source —
 * texture clones share the image data but carry their own UV transforms — so
 * the maps have to be disposed with the material and cannot be left to it.
 */
export function disposeExtrudedAluminumMaterial(
  material: THREE.MeshStandardMaterial,
): void {
  material.normalMap?.dispose();
  material.roughnessMap?.dispose();
  parallaxMaps.get(material)?.dispose();
  parallaxMaps.delete(material);
  material.dispose();
}

/**
 * Grooved aluminium for Kantenschutz (`AngleSectionRenderer`). The legs are
 * `ExtrudeGeometry` faces whose cap UVs are already in millimetres, with the
 * length along U and the leg depth along V, so one texture tile is stretched
 * across exactly one leg depth and the {@link GROOVE_POSITIONS} land where
 * they say they do.
 *
 * @param depth Leg depth in millimetres.
 * @param flipAcrossLeg Set for a leg whose V axis runs from the free edge
 *   inwards instead of from the inner corner outwards. The two legs of an angle
 *   are built from differently wound shapes and one of them is rotated, so
 *   without this the pattern is mirrored on one leg and only lines up with the
 *   rivets on the other.
 */
export function getAngleSectionAluminumMaterial(
  depth: number,
  flipAcrossLeg = false,
): THREE.MeshStandardMaterial {
  const spanMm = Math.max(depth, 1);
  const maps = getSurfaceMaps(ANGLE_SECTION_SURFACE_CONFIG, {
    spanMm,
    groovePositions: GROOVE_POSITIONS,
    grooveHalfWidth: GROOVE_HALF_WIDTH_MM / spanMm,
    dieLineAmplitude: 1 / ANGLE_SECTION_SURFACE_CONFIG.normalScale,
  });

  // One tile spans the leg; clamping keeps the end grooves from bleeding round.
  //
  // U is scaled to the same physical density as V even though the pattern does
  // not vary along it. Leaving U at 1 means one tile per millimetre — hundreds
  // of repeats along a half-metre profile — and mip selection takes the largest
  // derivative of the two axes, so that lone number would drag the whole
  // texture down to a coarse mip and smear the grooves away.
  // From `spanMm`, not `depth`: the maps above were baked for a leg of that
  // span, so scaling the UVs by the raw depth would put the grooves at a
  // different pitch than the one they were positioned at for any leg the
  // clamp actually caught.
  const scale = 1 / spanMm;
  for (const map of [maps.normalMap, maps.roughnessMap, maps.reliefMap]) {
    map.wrapT = THREE.ClampToEdgeWrapping;
    if (flipAcrossLeg) {
      map.repeat.set(scale, -scale);
      map.offset.set(0, 1);
    } else {
      map.repeat.set(scale, scale);
    }
  }

  // Both axes at the same physical density (above), so a unit of transformed
  // UV is one leg span either way; the flip only changes its sign, which the
  // tangent frame — built from the transformed UV's derivatives — follows.
  return createExtrudedAluminumMaterial(maps, ANGLE_SECTION_SURFACE_CONFIG, {
    u: spanMm,
    v: spanMm,
  });
}

/**
 * Grooved aluminium for the closing profiles (`ProfileRenderer`).
 *
 * The same finish as the Kantenschutz, laid out the same way: one tile spans
 * the whole developed cross-section, and the grooves sit on it at the fractions
 * the caller measured for the outer faces — two per face, see
 * {@link PROFILE_GROOVE_POSITIONS} — with the die lines and die streaks of the
 * mill finish running the full length of the part around them.
 *
 * `ProfiledContourGeometry` lays out U as normalised developed arc length
 * across the cross-section and V along the run, so a millimetre of profile is a
 * millimetre of texture wherever on the section it sits, and a groove given in
 * millimetres lands at a fraction of the section (`GROOVE_HALF_WIDTH_MM /
 * spanMm`). The section closes on itself, so the tile has to be periodic in U
 * — see {@link cyclicDistance} — and the die lines are; the grooves never
 * approach U = 0, which falls on the channel wall the panel hides.
 *
 * V is left at one repeat: the pattern does not vary along the extrusion, and a
 * tile that spans the whole run is the coarsest derivative of the two, so mip
 * selection is decided by U — the axis that carries the detail.
 *
 * @param sectionWidthMm `ProfiledContourGeometry.sectionWidth`: the distance
 *   walked along the cross-section, not its bounding box.
 * @param grooveU Groove centres as developed U on that section, from
 *   `ProfiledContourGeometry.sectionU`.
 */
export function getProfileAluminumMaterial(
  sectionWidthMm: number,
  grooveU: readonly number[],
): THREE.MeshStandardMaterial {
  const spanMm = Math.max(sectionWidthMm, 1);
  const config = profileSurfaceConfig(spanMm);
  const maps = getSurfaceMaps(config, {
    spanMm,
    // Rounded so that two sections differing in the fifth decimal of a
    // groove position share a bake; a texel is 1 / 4096 of the span.
    groovePositions: grooveU.map((u) => Math.round(u * 1e4) / 1e4),
    grooveHalfWidth: GROOVE_HALF_WIDTH_MM / spanMm,
    dieLineAmplitude: 1 / config.normalScale,
  });

  // One unit of U is the whole section. V spans the run, whose length the
  // material does not know — and does not need to, the pattern being constant
  // along it, so no parallax on that axis.
  return createExtrudedAluminumMaterial(maps, config, { u: spanMm, v: 0 });
}
